import { generateKeypair } from '../shared/sign';
import { tokenFor } from '../shared/token';

/* ─────────────────────────────────────────────
   Ambient CDN globals + DOM helpers
   ───────────────────────────────────────────── */
// Leaflet and qrcode-generator load from CDN <script> tags in index.html;
// they're untyped runtime globals, so `any` is the honest type.
declare const L: any;
// qrcode CDN global — used by ./ui/share-modal

import { $, qsa } from './ui/dom';
import { showToast } from './ui/toast';
import { ANIMALS } from './ui/animals';
import { purgeSelfCohortEcho } from './state/cohort-model';
import { setConsoleTarget, setAuthReady } from './state/auth-state';
import { state, getLeafletMap } from './state/app-state';
import { MOCK } from './state/device';
import {
  CADENCE_CHOICES,
  loadStoredToken, clearStoredToken,
  loadKeypair, saveKeypair, clearKeypair,
  loadActiveSession, clearActiveSession,
  loadCadence, setCadence,
  loadDeviceHistory, findInHistory, rememberDevice, forgetFromHistory,
  isPendingDevice,
} from './storage/persistence';
import {
  apiCreateDevice,
  apiGetPings,
  ensureRegistered,
  reconcileCreatedAt,
  decodeToken,
  setEnsureRenderHeaderCb,
} from './api/client';
// apiAuthMe, apiListClaims, apiClaimChallenge, apiClaimAccept, apiClaimRevoke,
import { fetchWorld, stopWorldLive, getWorldPollTimer } from './map/world';
import {
  ensureMap, drawRoutes,
  getTempPingMarker, setTempPingMarker, setPendingMapFit,
  setOpenDeviceConsoleCb, setRenderSessionsCb,
} from './map/tracker';
import {
  stopConsolePoll,
  exitDrawMode, getTeamDrawMode,
} from './map/team-layer';
import { buildShareUrl } from './ui/share-modal';
import {
  setRefreshAuthUiCb, setCopyTextCb,
} from './ui/group-claim';
import {
  openDeviceConsole,
  setOpenDrawerDeviceCb, setOpenShareModalCb,
  setRenameCurrentDeviceCb, setClaimCurrentDeviceCb,
  setOpenBackupExportCb, setUnclaimCurrentDeviceCb,
  setChangeVisibilityCb, setChangeCohortScopeCb,
  setCopyTextCb as setDevicePanelCopyTextCb,
  setForgetCurrentDeviceCb,
} from './ui/device-panel';
import {
  renderSessions, renderDeviceHeader,
  setSyncReplayCb, setEditCurrentDeviceCb, setRenderDrawerActiveCb, setGetLastRecPingCb,
} from './ui/sidebar';
import {
  updateRecordUi, startRecording, stopRecording,
  setEnsureRegisteredCb, getLastRecPing,
} from './ui/recording';
import {
  claimCurrentDevice, unclaimCurrentDevice,
  changeVisibility, changeCohortScope, renameCurrentDevice,
  initAuth,
  setAccountRefreshAuthUiCb, setOpenDrawerCb, setCloseDrawerCb,
  setShowCb, setActivateDeviceCb, setOpenBackupRestoreCb,
  setDecodeTokenCb, setGetDrawerViewCb,
} from './ui/account';
import {
  openBackupExport, openBackupRestore, initBackupModal,
  setBackupCloseDrawerCb, setBackupDecodeTokenCb, setBackupActivateDeviceCb,
} from './ui/backup-modal';
import {
  mintHighEntropyDevice, copyText,
  setHighEntropyOpenDrawerCb, setHighEntropyCloseDrawerCb, setHighEntropyActivateDeviceCb,
} from './ui/high-entropy';
import {
  renderPicker, renderSlots,
  activateDevice, animatePickerOut,
  refreshRecentDevicesButton,
  newDevice, editCurrentDevice,
  setPickerShowCb, setPickerCloseDrawerCb, setPickerEnsureRegisteredCb,
  setPickerReconcileCreatedAtCb, setPickerResetGroupPollLastNowCb,
  setPickerRepollGroupCb,
} from './ui/picker';
import {
  parseShareHash, enterShareViewer,
  shareViewerTick,
  getShareViewerPollTimer as _svPollTimer,
  getShareViewerToken as _svToken,
  setShareViewerSyncReplayCb, setShareViewerShowCb,
} from './ui/share-viewer';
import {
  syncReplay, initReplayControls,
  setReplayGetDrawerViewCb,
} from './ui/replay';
// replayRerender, replayPlay, replayPause, replayFrame, replayHide,
// replayUpdateUi, replayAtEdge, replayStopLoop are internal to ./ui/replay
import {
  setAdminShowCb,
} from './ui/admin';
// renderAdmin, renderAdminError, renderAdminDenied, doSweep, doReset,
// askTypedConfirm, adminPost, adminStat, adminAction, deletedTotal are internal to ./ui/admin
import {
  pollGroup,
  startCohortLive, stopCohortLive,
  getGroupPollTimer, resetGroupPollLastNow,
} from './routing/polling';
// startGroupPoll, stopGroupPoll are internal to ./routing/polling
import {
  routeJoinHash, routeJoinGroupHash,
  routeWorldHash, routeAdminHash,
  setHashShowCb,
} from './routing/hash';
import {
  show, openDrawer, closeDrawer, setDrawerView, renderDrawerActive, refreshAuthUi,
  forgetCurrentDevice, drawerView, drawerEl, drawerScrim,
  setRenderSettingsPanelCb,
  type DrawerView,
} from './routing/nav';

/* ─────────────────────────────────────────────
   Shared shapes
   ───────────────────────────────────────────── */
// Animal, ANIMALS, byKey, byShort, ANIMAL_KEYS, isHighEntropyDevice,
// DeviceMeta imported from ./state/device; CohortEntry, ActiveSession imported from ./state/app-state

/* ─────────────────────────────────────────────
   Backend wiring
   ───────────────────────────────────────────── */
// apiPostPings, apiGetPings, apiStartSession, apiStopSession, apiMintShare,

// MOCK, DeviceMeta imported from ./state/device

// groupSessionsLocal, GAP_MIN imported from ./state/cohort-model

// AppState interface, state imported from ./state/app-state

// ── Team overlays (ADR 0026/0027): the active claimed group's markers, geofence
// shapes, and recent breach events, fed by an ADDITIVE GET /groups/:g/console
// poll (pollConsole) that runs ALONGSIDE the cohort `recent` poll. This object
// drives ONLY the team map layer + breach toasts; it NEVER touches state.cohort,
// drawRoutes, or pollGroup — teammate trails stay exactly as today (coarsened
// `recent`). Absent/ungated ⇒ everything empty and no layer is drawn.
// team is imported from ./state/team
// groupPollTimer, groupPollLastNow, pollGroup, startGroupPoll, stopGroupPoll,

// tokenFor (offline device-token fallback) is imported from ../shared/token,
// the single source shared with the server-side issuer.

/* ─────────────────────────────────────────────
   COHORT — group cohort polling + colour helper
   ───────────────────────────────────────────── */

// COHORT_PALETTE, colorForToken imported from ./state/cohort-model

// ── High-entropy (non-animal) device rendering ─────────────────────────────
// ANIMAL_KEYS, isHighEntropyDevice, segAvatarsHtml, deviceDisplayName



// dedupePings, COHORT_CLIENT_CAP, trimCohort, isSelfCohortDevice, purgeSelfCohortEcho,
// mergeCohortDevice, seedLocalDeviceInCohort imported from ./state/cohort-model

/* ─────────────────────────────────────────────
   TEAM OVERLAYS — additive console poll (30 s)
   clearTeam, pollConsole, startConsolePoll, stopConsolePoll moved to ./map/team-layer
   ───────────────────────────────────────────── */

// Team layer render, draw controller, CRUD functions, breach toasts

/* ─────────────────────────────────────────────
   COHORT — live view control (30 s polling)
   startCohortLive, stopCohortLive moved to ./routing/polling
   ───────────────────────────────────────────── */

/* ─────────────────────────────────────────────
   ROUTING — simple two-screen
   show, openDrawer, closeDrawer, setDrawerView, renderDrawerActive, refreshAuthUi,
   forgetCurrentDevice, drawerView moved to ./routing/nav
   ───────────────────────────────────────────── */

/* ─────────────────────────────────────────────
   PICKER
   renderPicker, renderSlots, renderCombo, pickAnimal, activateDevice,
   animatePickerOut, renderRecentDevices, useRecentDevice, forgetRecentDevice,
   refreshRecentDevicesButton, defaultTarget, effectiveTarget, newDevice,
   editCurrentDevice moved to ./ui/picker
   ───────────────────────────────────────────── */


/* ─────────────────────────────────────────────
   RECENT DEVICES (picker)
   refreshRecentDevicesButton, renderRecentDevices, useRecentDevice,
   forgetRecentDevice moved to ./ui/picker
   ───────────────────────────────────────────── */

/* ─────────────────────────────────────────────
   MAP
   ensureMap, drawRoutes, selectSession, focusPing, sessionColorKey,
   drawAccuracyCircle, bindDevicePinPopup, pickInitialMapView,
   routeLayers, tempPingMarker, pendingMapFit moved to ./map/tracker
   ───────────────────────────────────────────── */

/* ─────────────────────────────────────────────
   WORLD MAP — session-required, per-caller world view (ADR 0018)
   worldMap, worldMarkers, worldPollTimer, worldMoveTimer, worldInFlight,
   ensureWorldMap, worldBbox, setWorldSignedOut, fetchWorld,
   renderWorldDevices, startWorldLive, stopWorldLive moved to ./map/world
   ───────────────────────────────────────────── */

// renderSessions, renderSessionsList, renderUngroupedList, renderByDeviceList,


/* ─────────────────────────────────────────────
   RECORDING
   ───────────────────────────────────────────── */

/* ─────────────────────────────────────────────
   NAVIGATION DRAWER (right-anchored; replaces the ⋮ popover)
   One |||-triggered drawer shared by both screens. `nav` lists destinations;
   Map / New device leave the drawer via show(); the lighter surfaces
   (Devices, Account, Device, Settings, a shared-link entry) swap in as
   sub-views behind a Back affordance, keeping the map visible behind the scrim.
   ───────────────────────────────────────────── */
// show, openDrawer, closeDrawer, setDrawerView, renderDrawerActive, refreshAuthUi,


// The Device sub-view: per-device actions — Share, claim/visibility (owner),
// Forget. Share is also a first-class button in the record strip next to
// Start/Stop (#open-share), matching Android.
// ── Command-console target (ADR 0027, Phase 3) ──────────────────────────────
// The device console (drawer "device" sub-view) normally drives the LOCAL device.
// A map-pin "Console ›" can retarget it to another group device by token; null
// means "the local device" (today's behaviour). canIssue gates the send control:
// only the token's owner (myClaims) may enqueue — read-only otherwise (ADR 0025).
// _consoleTarget, getConsoleTarget, setConsoleTarget imported from ./state/auth-state
// renderDevicePanel, openDeviceConsole, VIS_OPTIONS, COHORT_PRECISION_OPTIONS,


// currentGroupPath, currentGroupClaimState, probeGroupClaim, groupShareBody,
// claimCurrentGroup, revokeCurrentGroupClaim, mintInviteForCurrentGroup,

// The Settings sub-view: Log cadence (the sole control, mirroring Android;
// persisted — DECIDE-2) and an About card.
function renderSettingsPanel(){
  const box = $('settings-box');
  if(!box) return;
  const seg = CADENCE_CHOICES.map(sec => {
    const label = sec < 60 ? `${sec}s` : `${sec / 60}m`;
    return `<button class="seg-btn${sec === state.cadenceSec ? ' is-on' : ''}" data-cad="${sec}">${label}</button>`;
  }).join('');
  box.innerHTML =
    `<div class="set-card"><div class="set-title">Log cadence</div>` +
    `<div class="set-desc">How often your position is recorded while tracking. A slower ` +
    `cadence saves battery; a faster one gives a denser trail.</div>` +
    `<div class="seg-row">${seg}</div>` +
    `<div class="set-note">Applies live — including to a recording already in progress.</div></div>` +
    `<div class="set-card"><div class="set-title">About</div>` +
    `<div class="set-desc">Trail — a privacy-first location tracker. It records a signed ` +
    `trail, shows your own pings, and mints read-only share links.</div></div>`;
  qsa('.seg-btn', box).forEach((b: any) => b.addEventListener('click', () => {
    setCadence(parseInt(b.dataset.cad, 10));
    if(drawerView === 'settings') renderSettingsPanel();
    updateRecordUi();
  }));
}

/* ─────────────────────────────────────────────
   AUTH + CLAIM (Google sign-in, device ownership) — ADR 0014
   renderAccountPanel, renderGisButton, renderMyDevicesList, refreshMyClaims,
   claimCurrentDevice, unclaimCurrentDevice, changeVisibility, changeCohortScope,
   renameCurrentDevice, setCurrentDeviceTag, onGoogleCredential, onSignOut,
   openClaimedDevice, removeClaimedDevice, promptJoinPrivateGroup, initAuth,
   GOOGLE_CLIENT_ID, gisInited moved to ./ui/account
   ───────────────────────────────────────────── */
// _authUserId, getAuthUserId, setAuthUserId, _myClaims, getMyClaims, setMyClaims imported from ./state/auth-state

/* ─────────────────────────────────────────────
   REMOTE COMMAND CONSOLE (ADR 0024 web spec, rev 3) + GROUP CLAIM (ADR 0025)
   Owner-session-gated: pick a query/command (scoped to what the device has
   advertised), enqueue it, and watch the response surface in a durable feed
   that also replays on the trail's time scrubber. The device key is never
   involved — these are session+claim reads/writes.
   ───────────────────────────────────────────── */
// COMMAND_CATALOG, apiEnqueueCommand, apiGetDeviceCommands, formatCommandResult,
// apiGroupClaim, apiGroupClaimRevoke, apiMintInvite, apiGetGroupConsole,
// apiCreateMarker, apiUpdateMarker, apiDeleteMarker,

// The open device drawer's command console: cached capabilities + history, plus
// a poll that accelerates to ~5s while any row is pending (bounded by the 5-min
// TTL), else the normal 30s cadence. One timer, torn down on drawer close /
// view change.
// stopCommandConsole, commandConsoleHasPending, pollCommandConsole,
// startCommandConsole, renderCommandControls, renderCommandFeed,

// claimCurrentDevice, unclaimCurrentDevice, changeVisibility, changeCohortScope,
// renameCurrentDevice, setCurrentDeviceTag, onGoogleCredential, onSignOut,
// renderGisButton, openClaimedDevice, renderMyDevicesList, renderAccountPanel,

// Signed cohort-scope setter — mirrors apiMintShare (key possession, no
// session). The device's ed25519 key signs canonicalCohortScope; the server
// verifies and writes devices.cohort_range / cohort_precision.

// openBackupExport, openBackupRestore, renderBackupList, deleteBackup, selectBackup,
// doExport, doRestore, setBackupStatus, passStrength, updatePassMeter,



/* ─────────────────────────────────────────────
   REPLAY — time scrubber shared by the owner map + share viewer
   ─────────────────────────────────────────────
   A single cutoff `T`: only pings with t <= T render. Infinity means "live
   edge" (show everything, follow new pings). Both surfaces clip their pings
   through replayClip() and re-render via replayRerender(); syncReplay()
   keeps the [minT,maxT] range + control visibility in sync from the two
   render chokepoints (renderSessions / applyShareViewerPings). */
// REPLAY_STEPS, REPLAY_SWEEP_MS, replay imported from ./state/replay

// syncReplay, replayRerender, replayPlay, replayPause, replayFrame,
// replayHide, replayUpdateUi, replayAtEdge, replayStopLoop, initReplayControls

/* ─────────────────────────────────────────────
   SHARE MODAL
   ───────────────────────────────────────────── */

// ensureBreachStack, geofenceById, fenceCenter, panToFence, showBreachToast

// parseShareHash, enterShareViewer, abortShareViewer, shareViewerTick,
// applyShareViewerPings, renderShareViewerTrail, renderShareViewerHeader,
// startShareViewerPolling, stopShareViewerPolling, stopShareViewer,

/* ─────────────────────────────────────────────
   BOOT — listener wiring, Init block, hashchange, visibilitychange
   ───────────────────────────────────────────── */
async function boot(){
  $('randomize').addEventListener('click', ()=>{
    const r = () => ANIMALS[Math.floor(Math.random()*ANIMALS.length)].key;
    state.slots = [r(),r(),r()];
    state.targetOverride = null;
    renderSlots();
  });

  $('create-cta').addEventListener('click', async ()=>{
    const cta = $('create-cta');
    if(cta.disabled) return;
    // Guarded by the disabled CTA: this handler only runs with 3 filled
    // slots, so the slot/group arrays hold no nulls here.
    const animals = state.slots.slice() as string[];
    const group: string[] | null = state.group.every(Boolean) ? (state.group.slice() as string[]) : null;

    cta.disabled = true;

    // If this browser previously created this exact (animals, group) combo and
    // we still hold its signing key (or it's a pending offline mint), restore
    // that device — same token, pings, identity — instead of minting a new one.
    // A re-pick would otherwise hit a PRIMARY KEY collision in D1 and silently
    // mint a different attempt-salted token, stranding the earlier pings.
    // Since signed ingest, a device created in another browser shares the same
    // animal-derived token but can't post from here without its private key, so
    // that case falls through to a fresh create.
    const prior = findInHistory(animals, group);
    if(prior && (loadKeypair(prior.token) || isPendingDevice(prior.token))){
      animatePickerOut(() => activateDevice(prior, /*restored*/ true));
      return;
    }
    if(prior){
      showToast('this device was created in another browser — making a new one');
      forgetFromHistory(prior.token);
      // fall through to the fresh-create path below
    }

    // Fresh create: register server-side, falling back to a client-derived
    // token so the demo survives offline / API outage. Remember the device so
    // the next pick of the same combo restores it.
    let device: any;
    try {
      const res = await apiCreateDevice(animals, group);
      device = { token: res.token, animals, group, created_at: res.created_at };
      rememberDevice(device);
    } catch (err) {
      console.warn('[locus] device API unavailable; using client-side token', err);
      const token = tokenFor(animals, group);
      // Mint a keypair now and flag the device pending so ensureRegistered()
      // can adopt it server-side once connectivity returns — instead of leaving
      // a permanently keyless, unsyncable token.
      device = { token, animals, group, created_at: Date.now(), pending: true };
      try { saveKeypair(token, await generateKeypair()); }
      catch (e) { console.warn('[locus] offline keypair generation failed', e); }
      rememberDevice(device);
      showToast('offline — device will sync when reconnected');
    }
    animatePickerOut(() => activateDevice(device, /*restored*/ false));
  });

  $('brand-home').addEventListener('click', ()=>{
    // tap brand to randomize (small playful affordance)
    $('randomize').click();
  });
  $('brand-map').addEventListener('click', ()=> show('create'));

  $('record-btn').addEventListener('click', ()=>{
    if(state.recording || state.geoRequesting) stopRecording(); else startRecording();
  });

  // Drawer wiring.
  $('menu-btn')?.addEventListener('click', (e: any) => { e.stopPropagation(); openDrawer('nav'); });
  $('menu-btn-create')?.addEventListener('click', (e: any) => { e.stopPropagation(); openDrawer('nav'); });
  $('menu-btn-world')?.addEventListener('click', (e: any) => { e.stopPropagation(); openDrawer('nav'); });
  // World map topbar: back to the tracker map, and the signed-out sign-in CTA
  // (opens the Account drawer where the Google button lives).
  $('world-back')?.addEventListener('click', () => show('map'));
  $('world-signin')?.addEventListener('click', () => openDrawer('account'));

  /* ─────────────────────────────────────────────
     ADMIN CONSOLE (operator-only, #/admin) — ADR 0021
     Reachable only by typing #/admin (not in the nav). enterAdmin() does the
     auth-gated stats fetch; a non-admin gets the API's neutral 404 → "not
     authorized", never confirming the route exists. Destructive actions go
     through a typed-confirm overlay (RESET / NUKE / TRIM); dead-data sweeps
     (shares / sessions / orphans) fire on a single click.
     ───────────────────────────────────────────── */
  $('admin-back')?.addEventListener('click', () => show('map'));

  // enterAdmin, renderAdmin, renderAdminError, renderAdminDenied, doSweep, doReset,

  drawerScrim.addEventListener('click', closeDrawer);
  $('drawer-active').addEventListener('click', () => { setConsoleTarget(null); setDrawerView('device'); });
  qsa('[data-back]', drawerEl).forEach((b: any) => b.addEventListener('click', () => setDrawerView('nav')));
  qsa('.drawer-item', drawerEl).forEach((it: any) => it.addEventListener('click', () => {
    const dest = it.dataset.dest;
    if(dest === 'map'){ closeDrawer(); show('map'); }
    else if(dest === 'world'){ closeDrawer(); show('world'); }
    else if(dest === 'create'){ closeDrawer(); newDevice(); }
    else setDrawerView(dest as DrawerView);
  }));
  $('shared-open')?.addEventListener('click', () => {
    const raw = ($('shared-input').value || '').trim();
    if(!raw) return;
    // Accept a full URL (…/#/s/<token>) or a bare token.
    const m = /#\/s\/([A-Za-z0-9_-]{1,64})/.exec(raw);
    const token = m ? m[1] : (/^[A-Za-z0-9_-]{1,64}$/.test(raw) ? raw : '');
    if(!token){ showToast('not a valid share link'); return; }
    closeDrawer();
    // Navigate to the share hash; the hashchange handler is the single entry
    // point into the viewer, so an already-open viewer is reloaded into the new
    // share and the entry/teardown logic stays in one place.
    location.hash = `#/s/${token}`;
  });
  document.addEventListener('keydown', (e: any) => {
    if(e.key === 'Escape' && drawerEl.classList.contains('is-open')) closeDrawer();
  });

  $('mint-private')?.addEventListener('click', () => mintHighEntropyDevice(null, { promptName: true }));

  // Sidebar view tabs
  qsa('.sb-tab').forEach(t => {
    t.addEventListener('click', ()=>{
      state.viewMode = t.dataset.view;
      if(getTempPingMarker() && getLeafletMap()){ getLeafletMap().removeLayer(getTempPingMarker()); setTempPingMarker(null); }
      if(state.viewMode === 'devices') startCohortLive(); else stopCohortLive();
      renderSessions();
      if(getLeafletMap()) drawRoutes();
    });
  });

  initReplayControls();

  /* ─────────────────────────────────────────────
     SHARE MODAL
     ───────────────────────────────────────────── */
  const shareModal = $('share-modal');
  $('open-share').addEventListener('click', ()=>{
    shareModal.classList.add('is-open');
    buildShareUrl();
  });
  $('share-close').addEventListener('click', ()=> shareModal.classList.remove('is-open'));
  shareModal.addEventListener('click', (e: any)=>{ if(e.target === shareModal) shareModal.classList.remove('is-open'); });

  // The picker's "recent devices" shortcut now opens the Devices drawer view.
  $('open-recent').addEventListener('click', ()=> openDrawer('devices'));

  qsa('.toggle-group').forEach(tg=>{
    const key = tg.dataset.tg;
    qsa('button', tg).forEach((b,i)=>{
      b.addEventListener('click', ()=>{
        qsa('button', tg).forEach(x=>x.classList.remove('active'));
        b.classList.add('active');
        (state.shareSel as any)[key] = i;
        buildShareUrl();
      });
    });
  });
  $('copy-url').addEventListener('click', async (e: any)=>{
    // Don't copy (or claim success on) the mint placeholder / a failed mint.
    const url = $('share-url').value;
    if(!url || url === 'minting…'){ showToast('no share link yet'); return; }
    try { await navigator.clipboard.writeText(url); }
    catch { showToast('copy failed — long-press to copy'); return; }
    const b = e.currentTarget; b.classList.add('copied'); b.textContent='copied';
    setTimeout(()=>{ b.classList.remove('copied'); b.textContent='copy'; }, 1300);
    showToast('share link copied');
  });

  window.addEventListener('resize', ()=> { updateRecordUi(); });
  // Esc cancels an in-progress team draw/drop (always returns to idle, clears preview).
  window.addEventListener('keydown', (e) => { if(e.key === 'Escape' && getTeamDrawMode() !== 'idle') exitDrawMode(); });

  /* Init */
  renderPicker();
  renderSlots();
  refreshRecentDevicesButton();
  // Restore the persisted log cadence into state (the editable control lives in
  // Settings; the record strip shows it as a readout via updateRecordUi).
  const _savedCadence = loadCadence();
  if(_savedCadence != null){ setCadence(_savedCadence); updateRecordUi(); }
  // Resolve any existing server session so the account menu boots in the right
  // state (signed-in vs the GIS button). Independent of the device flow. The
  // promise is stored in auth-state so map/world.ts (and the join router)
  // can await it without importing main.
  setAuthReady(initAuth());

  window.addEventListener('hashchange', () => {
    // In share-viewer mode the chrome is hidden (data-mode) and a 30 s poll runs
    // on a separate state model with none of the viewer's own app state hydrated
    // (a cold-boot share link never ran boot()). So ANY hash navigation while in
    // that mode — Back/Forward off the share, or to a different share — is handled
    // by a reload: boot()/the share branch re-enter cleanly for the new hash and
    // the poll dies with the old document. Without this the poll leaks and the
    // chrome stays hidden with no way out (the ADR-0012 poll-leak class). Entering
    // a share goes through the branch below (data-mode is not set yet), so the
    // entry navigation itself is never caught here.
    if(document.body.dataset.mode === 'share-viewer'){ location.reload(); return; }
    if(routeWorldHash()) return;
    if(routeAdminHash()) return;
    if(routeJoinGroupHash()) return;
    if(routeJoinHash()) return;
    // Entering a share: the in-app "Open" button sets the hash (single entry path),
    // and Back/Forward onto a #/s/ hash lands here too. End any live tracker/world
    // feed before handing the map to the viewer.
    const share = parseShareHash(location.hash);
    if(share){
      stopWorldLive(); stopCohortLive(); stopConsolePoll(); stopRecording();
      enterShareViewer(share);
      return;
    }
    // The route* helpers only handle ENTERING their screen. Pressing browser
    // Back/Forward OFF world/admin (the hash is no longer theirs) must leave the
    // screen so its poll stops / it doesn't linger while the URL says #/map.
    if($('screen-world').classList.contains('is-active')) show('map');
    if($('screen-admin').classList.contains('is-active')) show('map');
  });

  // One module-level visibilitychange listener (F1). Each poller's tick early-
  // returns while document.hidden, so on re-show we fire an immediate catch-up
  // for whichever poller(s) are currently live (a non-null timer handle == that
  // poller is active), then the normal 30 s cadence resumes. This only READS the
  // existing timer handles — it never starts/stops a poller, so it adds no new
  // poll-leak surface (ADR 0012 class); registered once for the page lifetime.
  document.addEventListener('visibilitychange', () => {
    if(document.hidden) return;
    if(getGroupPollTimer()) void pollGroup();
    if(getWorldPollTimer()) void fetchWorld();
    if(_svPollTimer() && _svToken()) void shareViewerTick(_svToken()!);
  });

  // Wire the device-console callback so bindDevicePinPopup (in tracker.ts once
  // moved) can open the console drawer without importing main.ts.
  setOpenDeviceConsoleCb(openDeviceConsole);
  // Wire the renderSessions callback so selectSession (in tracker.ts once moved)
  // can call renderSessions() without importing main.ts.
  setRenderSessionsCb(renderSessions);
  // Wire callbacks into ui/sidebar.ts so it can call main.ts functions without
  // a circular import.
  setSyncReplayCb(syncReplay);
  setEditCurrentDeviceCb(editCurrentDevice);
  setRenderDrawerActiveCb(renderDrawerActive);
  setGetLastRecPingCb(getLastRecPing);
  setEnsureRegisteredCb(ensureRegistered);
  // Wire callbacks into ui/group-claim.ts so it can call main.ts functions without
  // a circular import.
  setRefreshAuthUiCb(refreshAuthUi);
  setCopyTextCb(copyText);
  // Wire callbacks into ui/device-panel.ts so it can call main.ts functions without
  // a circular import.
  setOpenDrawerDeviceCb(() => openDrawer('device'));
  setOpenShareModalCb(() => { closeDrawer(); shareModal.classList.add('is-open'); buildShareUrl(); });
  setRenameCurrentDeviceCb(renameCurrentDevice);
  setClaimCurrentDeviceCb(claimCurrentDevice);
  setOpenBackupExportCb(openBackupExport);
  setUnclaimCurrentDeviceCb(unclaimCurrentDevice);
  setChangeVisibilityCb(changeVisibility);
  setChangeCohortScopeCb(changeCohortScope);
  setDevicePanelCopyTextCb(copyText);
  setForgetCurrentDeviceCb(forgetCurrentDevice);
  // Wire callbacks into ui/account.ts so it can call main.ts functions without
  // a circular import.
  setAccountRefreshAuthUiCb(refreshAuthUi);
  setOpenDrawerCb((view: string) => openDrawer(view as any));
  setCloseDrawerCb(closeDrawer);
  setShowCb(show);
  setActivateDeviceCb(activateDevice);
  setOpenBackupRestoreCb(openBackupRestore);
  setDecodeTokenCb(decodeToken);
  setGetDrawerViewCb(() => drawerView);
  // Wire callbacks into ui/backup-modal.ts so it can call main.ts functions without
  // a circular import.
  setBackupCloseDrawerCb(closeDrawer);
  setBackupDecodeTokenCb(decodeToken);
  setBackupActivateDeviceCb(activateDevice);
  initBackupModal();
  // Wire callbacks into ui/high-entropy.ts so it can call main.ts functions without
  // a circular import.
  setHighEntropyOpenDrawerCb((view: string) => openDrawer(view as any));
  setHighEntropyCloseDrawerCb(closeDrawer);
  setHighEntropyActivateDeviceCb(activateDevice);
  // Wire callbacks into ui/picker.ts so it can call main.ts functions without
  // a circular import.
  setPickerShowCb(show);
  setPickerCloseDrawerCb(closeDrawer);
  setPickerEnsureRegisteredCb(ensureRegistered);
  setPickerReconcileCreatedAtCb(reconcileCreatedAt);
  setPickerResetGroupPollLastNowCb(resetGroupPollLastNow);
  setPickerRepollGroupCb(pollGroup);
  // Wire the drawerView accessor into ui/replay.ts so it can read drawerView
  // without importing main.ts (breaks the replay→main cycle).
  setReplayGetDrawerViewCb(() => drawerView);
  // Wire callbacks into ui/share-viewer.ts so it can call main.ts functions without
  // a circular import.
  setShareViewerSyncReplayCb(syncReplay);
  setShareViewerShowCb(show);
  // Wire callbacks into ui/admin.ts so it can call main.ts functions without
  // a circular import.
  setAdminShowCb(show);
  // Wire show() into routing/hash.ts so route* helpers can call it without
  // importing main.ts.
  setHashShowCb(show);
  // Wire the renderDeviceHeader callback into ensureRegistered (in api/client.ts
  // once moved) so the token-collision path can repaint the header without
  // api/ importing ui/.
  setEnsureRenderHeaderCb(renderDeviceHeader);
  // Wire renderSettingsPanel into routing/nav.ts (once moved) so setDrawerView
  // can call it without importing main.ts.
  setRenderSettingsPanelCb(renderSettingsPanel);

  // Share-viewer takes precedence over the regular boot flow — the SPA acts
  // as a read-only renderer for someone else's device. The wiring above still
  // runs in this path (the share viewer drives syncReplay via its injected
  // callback), but the device-hydrate / join flow below is skipped.
  const _shareHash = parseShareHash(location.hash);
  if(_shareHash){
    enterShareViewer(_shareHash);
    return;
  }

  // A join link mints into the picker/map flow behind a confirm dialog. The
  // `#/join-group/` (claimed, invite) and `#/join/` (open) prefixes are distinct;
  // try the invite form first.
  routeJoinGroupHash();
  routeJoinHash();
  // A `#/world` deep link / refresh lands on the world map (session-required;
  // the view itself gates on auth and shows the sign-in state if needed). Even
  // then we still hydrate the persisted device BELOW (H1) — without it, "Back to
  // my map" would land on an empty tracker map — we just skip routing to it.
  const worldRouted = routeWorldHash();
  // A `#/admin` deep link / refresh lands on the operator console (gated
  // server-side; a non-admin sees "not authorized"). Like world, it suppresses
  // the picker/map fallback below but we still hydrate the persisted device.
  const adminRouted = routeAdminHash();
  const offMyMap = worldRouted || adminRouted;
  const token = loadStoredToken();
  const decoded = decodeToken(token);
  if(!token || !decoded){
    // No persisted device → keep the seeded demo data + show the picker (unless
    // we're already showing the world map / admin console from a deep link).
    if(!offMyMap) show('create');
    return;
  }
  // Persisted device → reconstruct MOCK.device, route to the map, and
  // asynchronously hydrate pings from D1. The token doesn't encode the creation
  // time, so seed `created` from history when we have it (a device made in this
  // browser) — the self-read below reconciles it to the server's value either
  // way. Keeping it accurate from the start lets the first cohort poll de-dup
  // this device's own echo without waiting for the hydrate round-trip.
  const histCreated = loadDeviceHistory().find((d: any) => d.token === token)?.created_at;
  MOCK.device = {
    animals: decoded.animals,
    group: decoded.group,
    token,
    created: new Date(Number.isFinite(histCreated) ? histCreated : Date.now()).toISOString(),
  };
  // Backfill the device-history list for users upgrading from before the
  // history feature existed — guarantees the next "same animals" pick
  // restores this token instead of minting a new one.
  rememberDevice({ token, animals: decoded.animals, group: decoded.group, created_at: Date.now() });
  state.cohort.clear();
  resetGroupPollLastNow();
  MOCK.pings = [];
  // Resume a server-tracked session that was open at last reload. The user
  // hasn't pressed "start recording" yet — we just remember which session
  // any future pings belong to. They re-press start to actually begin.
  state.activeSession = loadActiveSession(token);
  if(!offMyMap) show('map');
  // Adopt a server row for a device minted offline on a prior visit before
  // we try to read its pings (a pending device has no row → would 404). May
  // swap the token on a collision; read the live token back afterwards.
  if(isPendingDevice(token)) await ensureRegistered(token);
  const liveToken = MOCK.device?.token || token;
  if(isPendingDevice(liveToken)){
    // Still offline / unregistered — keep the local device, nothing to
    // hydrate yet, and crucially skip the unknown-token teardown below.
    return;
  }
  try {
    const res = await apiGetPings(liveToken);
    MOCK.pings = res.pings;
    // The boot path reconstructs MOCK.device with a placeholder created time
    // (it isn't encoded in the token); the self-read carries the authoritative
    // one, so adopt it — the cohort self-dedup keys on created_at, and a poll
    // that already merged the self-echo gets purged here.
    reconcileCreatedAt(liveToken, res.created_at);
    // The self-read also carries the device display tag (ADR 0022); adopt it so
    // the header/drawer show the device's name from the first paint.
    if(MOCK.device && MOCK.device.token === liveToken) MOCK.device.tag = res.tag;
    purgeSelfCohortEcho();
    renderSessions();
    renderDeviceHeader();
    if(offMyMap && !$('screen-map').classList.contains('is-active')){
      // Deep-linked to #/world or #/admin: the tracker map is hidden (zero-size), so defer
      // the first draw+fit to the next show('map') instead of fitBounds()-ing a
      // collapsed div. If the user already tapped "Back to my map" during this
      // await, screen-map is active and we fall through to draw immediately.
      setPendingMapFit(true);
    } else {
      // This hydrate can resolve before show('map')'s deferred ensureMap()
      // runs, so create the map now (idempotent) and size it before drawing —
      // otherwise the first paint of a reloaded device's trail is dropped.
      ensureMap();
      if(getLeafletMap()) getLeafletMap().invalidateSize();
      drawRoutes({fit: true});
    }
  } catch (err: any) {
    console.warn('[locus] hydrate pings failed', err);
    if(err?.status === 404){
      // Server doesn't know this token — most likely the 0003 reshape wiped
      // the row. Tear down local state so the picker shows a fresh slate
      // instead of leaving the user stuck unable to record.
      clearStoredToken();
      clearKeypair(liveToken);
      forgetFromHistory(liveToken);
      clearActiveSession(liveToken);
      state.activeSession = null;
      MOCK.device = null;
      showToast('this device is unknown to the server');
      // Don't yank a world-map viewer / admin console to the picker; those
      // views are fine without a local device. They'll see the picker next time.
      if(!offMyMap) show('create');
    }
  }
}

void boot();
